import { ReactNode, useState } from 'react';
import {
  CheckCheck,
  Minimize2,
  Paperclip,
  Search,
  Send,
  Smile,
  X,
} from 'lucide-react';
import { toAbsoluteUrl } from '@/lib/helpers';
import { cn } from '@/lib/utils';
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  AvatarIndicator,
  AvatarStatus,
} from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Sheet,
  SheetBody,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import { ScrollArea } from '@/components/ui/scroll-area';

interface ChatUser {
  id: string;
  name: string;
  avatar: string;
  status: 'online' | 'offline' | 'away';
  lastMessage: string;
  lastMessageTime: string;
  unreadCount?: number;
}

interface Message {
  id: string;
  text: string;
  time: string;
  isOwn: boolean;
  read?: boolean;
}

interface ChatWindow {
  id: string;
  user: ChatUser;
  isMinimized: boolean;
  messages: Message[];
  messageInput: string;
}

// Chat Windows Manager (Global State)
const MAX_CHAT_WINDOWS = 3;
const chatWindows: ChatWindow[] = [];
const chatWindowsListeners: Array<() => void> = [];

function addChatWindow(user: ChatUser) {
  const existingIndex = chatWindows.findIndex((w) => w.id === user.id);
  
  if (existingIndex !== -1) {
    // Window already exists - move it to the end (most recent position) and un-minimize
    const existingWindow = chatWindows.splice(existingIndex, 1)[0];
    existingWindow.isMinimized = false;
    chatWindows.push(existingWindow);
    notifyChatWindowsListeners();
  } else {
    // New window - check if we need to remove oldest
    if (chatWindows.length >= MAX_CHAT_WINDOWS) {
      chatWindows.shift(); // Remove the oldest window
    }
    
    chatWindows.push({
      id: user.id,
      user,
      isMinimized: false,
      messages: [
        {
          id: '1',
          text: 'Hi! How are you?',
          time: '10:30 AM',
          isOwn: false,
        },
        {
          id: '2',
          text: "I'm doing great! How about you?",
          time: '10:32 AM',
          isOwn: true,
          read: true,
        },
      ],
      messageInput: '',
    });
    notifyChatWindowsListeners();
  }
}

function removeChatWindow(id: string) {
  const index = chatWindows.findIndex((w) => w.id === id);
  if (index !== -1) {
    chatWindows.splice(index, 1);
    notifyChatWindowsListeners();
  }
}

function toggleMinimizeChatWindow(id: string) {
  const window = chatWindows.find((w) => w.id === id);
  if (window) {
    window.isMinimized = !window.isMinimized;
    notifyChatWindowsListeners();
  }
}

function updateChatWindowMessage(id: string, message: string) {
  const window = chatWindows.find((w) => w.id === id);
  if (window) {
    window.messageInput = message;
    notifyChatWindowsListeners();
  }
}

function sendChatWindowMessage(id: string) {
  const window = chatWindows.find((w) => w.id === id);
  if (window && window.messageInput.trim()) {
    window.messages.push({
      id: Date.now().toString(),
      text: window.messageInput,
      time: new Date().toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
      }),
      isOwn: true,
      read: false,
    });
    window.messageInput = '';
    notifyChatWindowsListeners();
  }
}

function notifyChatWindowsListeners() {
  chatWindowsListeners.forEach((listener) => listener());
}

export function ChatSheet({ trigger }: { readonly trigger: ReactNode }) {
  const [searchQuery, setSearchQuery] = useState('');
  const [, forceUpdate] = useState({});

  // Subscribe to chat windows changes
  useState(() => {
    const listener = () => forceUpdate({});
    chatWindowsListeners.push(listener);
    return () => {
      const index = chatWindowsListeners.indexOf(listener);
      if (index !== -1) chatWindowsListeners.splice(index, 1);
    };
  });

  const chatUsers: ChatUser[] = [
    {
      id: '1',
      name: 'Jason Tatum',
      avatar: '/media/avatars/300-1.png',
      status: 'online',
      lastMessage: 'Hey! How are you doing?',
      lastMessageTime: '2m ago',
      unreadCount: 3,
    },
    {
      id: '2',
      name: 'Sarah Johnson',
      avatar: '/media/avatars/300-2.png',
      status: 'online',
      lastMessage: 'The project looks great!',
      lastMessageTime: '15m ago',
    },
    {
      id: '3',
      name: 'Mike Wilson',
      avatar: '/media/avatars/300-3.png',
      status: 'away',
      lastMessage: 'Can we schedule a meeting?',
      lastMessageTime: '1h ago',
      unreadCount: 1,
    },
    {
      id: '4',
      name: 'Emma Davis',
      avatar: '/media/avatars/300-4.png',
      status: 'online',
      lastMessage: 'Thanks for the update',
      lastMessageTime: '2h ago',
    },
    {
      id: '5',
      name: 'Chris Brown',
      avatar: '/media/avatars/300-5.png',
      status: 'offline',
      lastMessage: 'See you tomorrow!',
      lastMessageTime: 'Yesterday',
    },
  ];

  const filteredUsers = chatUsers.filter((user) =>
    user.name.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  const handleOpenChatWindow = (user: ChatUser) => {
    addChatWindow(user);
  };

  return (
    <>
      {/* Chat Drawer */}
      <Sheet>
        <SheetTrigger asChild>{trigger}</SheetTrigger>
        <SheetContent className="p-0 gap-0 sm:w-[400px] sm:max-w-none">
          <SheetHeader className="p-5 pb-4">
            <SheetTitle>Messages</SheetTitle>
          </SheetHeader>

          {/* Search Bar */}
          <div className="px-5 pb-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Search conversations..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>

          {/* User List */}
          <SheetBody className="p-0">
            <ScrollArea className="h-[600px]">
              <div className="divide-y divide-border">
                {filteredUsers.map((user) => (
                  <button
                    key={user.id}
                    onClick={() => handleOpenChatWindow(user)}
                    className="w-full px-5 py-4 flex items-start gap-3 hover:bg-accent/50 transition-colors text-left"
                  >
                    <div className="relative">
                      <Avatar className="size-11">
                        <AvatarImage
                          src={toAbsoluteUrl(user.avatar)}
                          alt={user.name}
                        />
                        <AvatarFallback>
                          {user.name
                            .split(' ')
                            .map((n) => n[0])
                            .join('')}
                        </AvatarFallback>
                        <AvatarIndicator className="-end-0.5 -bottom-0.5">
                          <AvatarStatus
                            variant={user.status}
                            className="size-3"
                          />
                        </AvatarIndicator>
                      </Avatar>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1">
                        <h4 className="text-sm font-semibold text-foreground">
                          {user.name}
                        </h4>
                        <span className="text-xs text-muted-foreground">
                          {user.lastMessageTime}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <p className="text-sm text-muted-foreground truncate pr-2">
                          {user.lastMessage}
                        </p>
                        {Boolean(user.unreadCount) && (
                          <span className="flex-shrink-0 inline-flex items-center justify-center size-5 text-xs font-semibold bg-primary text-primary-foreground rounded-full">
                            {user.unreadCount}
                          </span>
                        )}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </ScrollArea>
          </SheetBody>
        </SheetContent>
      </Sheet>

      {/* Floating Chat Windows */}
      {chatWindows.map((window, index) => (
        <div
          key={window.id}
          className="fixed bottom-0 z-50 w-[350px] bg-background border border-border rounded-t-lg shadow-2xl flex flex-col"
          style={{
            right: `${24 + index * 370}px`,
            height: window.isMinimized ? '60px' : '500px',
            transition: 'height 0.3s ease',
          }}
        >
          {/* Chat Window Header */}
          <div className="p-3 border-b border-border flex items-center justify-between bg-accent/30">
            <div className="flex items-center gap-2 flex-1 min-w-0">
              <Avatar className="size-8">
                <AvatarImage
                  src={toAbsoluteUrl(window.user.avatar)}
                  alt={window.user.name}
                />
                <AvatarFallback>
                  {window.user.name
                    .split(' ')
                    .map((n) => n[0])
                    .join('')}
                </AvatarFallback>
                <AvatarIndicator className="-end-0.5 -bottom-0.5">
                  <AvatarStatus variant={window.user.status} className="size-2.5" />
                </AvatarIndicator>
              </Avatar>
              <div className="flex-1 min-w-0">
                <h4 className="text-sm font-semibold truncate">{window.user.name}</h4>
                <p className="text-xs text-muted-foreground capitalize">
                  {window.user.status}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                mode="icon"
                size="sm"
                onClick={() => toggleMinimizeChatWindow(window.id)}
              >
                <Minimize2 className="size-3.5" />
              </Button>
              <Button
                variant="ghost"
                mode="icon"
                size="sm"
                onClick={() => removeChatWindow(window.id)}
              >
                <X className="size-3.5" />
              </Button>
            </div>
          </div>

          {/* Messages Area */}
          {!window.isMinimized && (
            <>
              <div className="flex-1 overflow-hidden">
                <ScrollArea className="h-full">
                  <div className="p-4 space-y-3">
                    {window.messages.map((message) => (
                      <div
                        key={message.id}
                        className={cn(
                          'flex',
                          message.isOwn ? 'justify-end' : 'justify-start',
                        )}
                      >
                        <div
                          className={cn(
                            'max-w-[75%] rounded-lg px-3 py-2',
                            message.isOwn
                              ? 'bg-primary text-primary-foreground'
                              : 'bg-accent text-foreground',
                          )}
                        >
                          <p className="text-sm">{message.text}</p>
                          <div
                            className={cn(
                              'flex items-center gap-1 mt-1',
                              message.isOwn ? 'justify-end' : 'justify-start',
                            )}
                          >
                            <span
                              className={cn(
                                'text-xs',
                                message.isOwn
                                  ? 'text-primary-foreground/70'
                                  : 'text-muted-foreground',
                              )}
                            >
                              {message.time}
                            </span>
                            {message.isOwn && (
                              <CheckCheck
                                className={cn(
                                  'size-3',
                                  message.read
                                    ? 'text-green-300'
                                    : 'text-primary-foreground/70',
                                )}
                              />
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              </div>

              {/* Message Input */}
              <div className="p-3 border-t border-border">
                <div className="flex items-center gap-2">
                  <Button variant="ghost" mode="icon" size="sm">
                    <Paperclip className="size-3.5" />
                  </Button>
                  <Button variant="ghost" mode="icon" size="sm">
                    <Smile className="size-3.5" />
                  </Button>
                  <Input
                    type="text"
                    placeholder="Type a message..."
                    value={window.messageInput}
                    onChange={(e) =>
                      updateChatWindowMessage(window.id, e.target.value)
                    }
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        sendChatWindowMessage(window.id);
                      }
                    }}
                    className="flex-1 h-9"
                  />
                  <Button
                    variant="mono"
                    mode="icon"
                    size="sm"
                    onClick={() => sendChatWindowMessage(window.id)}
                    disabled={!window.messageInput.trim()}
                  >
                    <Send className="size-3.5" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </div>
      ))}
    </>
  );
}